import type { Contact } from "./contact";
import type { ClientVehicle } from "./client_vehicle";

/** @deprecated Use tag_ids with OrgTag instead */
export type OrdreTag = "huile" | "diag" | "pieces_en_attente" | "accidente" | "assurance";

export type OrdreStatus =
  | "rdv"
  | "a_preparer"
  | "devis_initial"
  | "commande"
  | "pret"
  | "reception"
  | "en_cours"
  | "devis_supp"
  | "accord"
  | "acompte"
  | "fin_de_travaux"
  | "facturation"
  | "solde"
  | "solde_paye"
  | "disponible"
  | "restitue"
  | "arrete";

export type TrackingStep =
  | "carte_grise_manquante"
  | "en_attente_devis"
  | "devis_pret"
  | "en_attente_depot"
  | "vehicule_depose"
  | "devis_supplementaire"
  | "acompte_manquant"
  | "reparations_en_cours"
  | "travaux_termines"
  | "facture_disponible"
  | "solde_en_attente"
  | "paiement_valide"
  | "vehicule_disponible"
  | "vehicule_restitue";

export type OrdreFlag = "bloque" | "urgent";

export const ORDRE_FLAG_CONFIG: Record<OrdreFlag, { label: string; color: string; bg: string; icon: string }> = {
  bloque: { label: "Bloqué", color: "#DC2626", bg: "#FEE2E2", icon: "warning" },
  urgent: { label: "Urgent", color: "#D97706", bg: "#FEF3C7", icon: "flag" },
};

export interface OrdreIntervention {
  id: string;
  label: string;
  estimated_hours: number;
  assigned_to?: string;
  group?: string; // "initial" | "additional_0" | "additional_1" | ...
  forfait_code?: string; // copied from the catalog intervention; drives "Forfait Local" type on ServiceBox transfer
  /** "inclus" = billed on this RDV; "a_prevoir" = saved to the dossier for a later visit. Undefined is treated as "inclus". */
  status?: "inclus" | "a_prevoir";
  /** VIN of the vehicle this line belongs to — set when it goes "a_prevoir" so it is only re-proposed for the same vehicle. */
  vin?: string;
  /** When an "a_prevoir" line is later accepted into another ordre, that ordre's id is stamped here so it leaves the pending pool. */
  consumed_by_ordre_id?: string;
  consumed_at?: string;
}

/** An intervention is "inclus" unless explicitly marked "a_prevoir". */
export const isAPrevoir = (i: OrdreIntervention): boolean => i.status === "a_prevoir";
/** A pending à-prévoir line: à-prévoir and not yet consumed into a later ordre. */
export const isPendingAPrevoir = (i: OrdreIntervention): boolean =>
  i.status === "a_prevoir" && !i.consumed_by_ordre_id;

export interface InterventionGroupStatus {
  status: "draft" | "devis_sent" | "devis_signed" | "ready";
  extraction_id?: string;
  devis_token?: string;
}

export interface OrdreDocument {
  id: string;
  name: string;
  type: string;
  gcs_path?: string;
  created_at: string;
  /** "whatsapp" when the client sent the file, else undefined. */
  source?: string;
}

export interface OrdreTimelineEvent {
  id: string;
  date: string;
  user_name: string;
  user_color: string;
  action: string;
  type: "documents" | "photos" | "status_change" | "comment";
  sender_type?: "user" | "client" | "system";
  document_names?: string[];
  photo_urls?: string[];
  status_label?: string;
  status_type?: "success" | "warning" | "info";
  ordre_ref?: string;
}

export interface Ordre {
  id: string;
  org_id: string;
  dossier_id?: string;
  vehicle_id?: string;
  contact_ids?: string[];
  num: string;
  name: string;
  tags: OrdreTag[];
  tag_ids?: string[];
  status: OrdreStatus;
  restitution?: { seconds: number; nanoseconds?: number };
  reception_date?: { seconds: number; nanoseconds?: number };
  modele?: string;
  immatriculation?: string;
  campagne?: string;
  recall_campaigns?: { code: string; libelle: string; status: string }[];
  /** ISO timestamp of the last live ServiceBox recall check (throttles re-checks). */
  recall_checked_at?: string;
  /** @deprecated Auto-derived mirror of assigned_to_ids[0]. Read via getAssignedIds(); write assigned_to_ids. */
  assigned_to?: string;
  /** Canonical list of assigned users (source of truth for assignment). */
  assigned_to_ids?: string[];
  mobility_option?: string;
  rental_vehicle_id?: string;
  rental_reservation_id?: string;
  created_at: { seconds: number; nanoseconds?: number };
  notes?: string;
  vehicle?: ClientVehicle;
  contacts?: Contact[];
  interventions: OrdreIntervention[];
  documents?: OrdreDocument[];
  /** Set when a client sent a document via WhatsApp; cleared when the Documents tab is opened. */
  has_unseen_client_docs?: boolean;
  timeline?: OrdreTimelineEvent[];
  discussion_id?: string;
  internal_discussion_id?: string;
  tracking_step?: TrackingStep;
  devis_sign_token?: string;
  devis_sign_status?: string;
  devis_document_id?: string;
  devis_signer_name?: string;
  devis_signer_email?: string;
  client_rating?: number;
  client_rating_date?: string;
  amount?: number;
  labor_cost?: number;
  parts_cost?: number;
  devis_requested?: boolean;
  devis_signed?: boolean;
  acompte_requested?: boolean;
  acompte_paid?: boolean;
  solde_requested?: boolean;
  solde_paid?: boolean;
  intervention_groups?: Record<string, InterventionGroupStatus>;
  flags?: OrdreFlag[];
  flag_reason?: string;
  /** When true, the "Déposer la carte grise" step/CTA is hidden on the client tracking page
   *  (client hands the carte grise over by other means). Toggled from the ordre Actions menu. */
  carte_grise_not_required?: boolean;

  or_number?: string;
  servicebox_rdv_id?: string;
  servicebox_dossier_id?: string;
  /** OR number returned by Alpha DMS after transferring a signed devis. Set once the proxy successfully transfers the dossier. */
  alpha_or_number?: string;
  alpha_transferred_at?: string;
  /** ISO timestamp set when the RDV was "pre-transferred": created on ServiceBox
   *  (servicebox_dossier_id / servicebox_rdv_id set) WITHOUT the Alpha transfer, so there
   *  is deliberately no or_number yet. Batch recovery marker; a later normal transfer adds the OR. */
  servicebox_pretransferred_at?: string;
  booking_request?: {
    service?: string;
    intervention_type?: string;
    intervention?: string;
    mobility?: string;
    tire_option?: string;
    deposit_date?: string;
    deposit_time?: string;
    restitution_date?: string;
    restitution_time?: string;
    immatriculation?: string;
    km?: string;
    comments?: string;
    channel?: string;
    civilite?: string;
    nom?: string;
    prenom?: string;
    phone?: string;
    email?: string;
    code_postal?: string;
  };
}

/**
 * Canonical read of an ordre's assigned user ids.
 * Prefers the array; falls back to the legacy scalar `assigned_to` for docs
 * that predate the multi-assign migration.
 */
export function getAssignedIds(
  ordre: { assigned_to_ids?: string[]; assigned_to?: string } | null | undefined,
): string[] {
  if (!ordre) return [];
  if (ordre.assigned_to_ids && ordre.assigned_to_ids.length > 0) return ordre.assigned_to_ids;
  return ordre.assigned_to ? [ordre.assigned_to] : [];
}

/* LEGACY — removed from Ordre, managed separately
export interface OrdreContact {
  id: string;
  name: string;
  role: string;
  phone?: string;
  email?: string;
}

export interface OrdreVehicle {
  id: string;
  plate: string;
  marque?: string;
  modele?: string;
  coloris?: string;
  km?: number;
  vin?: string;
}

export interface OrdreAtelierCharge {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
}
*/
